import { useStore } from "@redotech/react-util/store";
import { localStore } from "@redotech/web-util/storage";
import jwtDecode from "jwt-decode";
import {
  ReactNode,
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CustomerContext } from "./CustomerContext";
import { UserContext } from "./user";

export interface Auth {
  teamId: string;
  userEmail?: string;
  token: string;
  expiration: Temporal.Instant;
}

export const AuthContext = createContext<Auth | undefined>(undefined);

export interface SetAuth {
  (token: string | undefined): void;
}

export const SetAuthContext = createContext<SetAuth | undefined>(undefined);

const authTokenStore = localStore("redo.customer_auth_token");

/**
 * Redirect to /login if not logged in
 */
export const AuthGuard = memo(function AuthGuard({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const auth = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth) {
      const params = new URLSearchParams(location.search);
      const newParams = new URLSearchParams();
      if (params.has("customer")) {
        newParams.set("customer", params.get("customer")!);
      }
      if (params.has("token")) {
        newParams.set("token", params.get("token")!);
      }
      if (params.has("order")) {
        newParams.set("order", params.get("order")!);
      }
      if (params.has("email")) {
        newParams.set("email", params.get("email")!);
      }
      newParams.set("next", location.pathname + "?" + params);

      navigate(`/returns-portal/login?${newParams}`);
    }
  }, [auth, location.pathname, navigate, location.search]);

  if (!auth) {
    return null;
  }

  return <>{children}</>;
});

/**
 * Redirect to /returns-portal if we don't have a customer id
 * We don't want a user without a customer id getting into the customer portal right now
 * If we have a customer id, wait to fully load the customer context before rendering children
 */
export const CustomerAccountAuthGuard = memo(function CustomerAccountAuthGuard({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const customerContext = useContext(CustomerContext);
  const userAuth = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!!userAuth && !userAuth.customerId) {
      navigate("/returns-portal");
    }
  }, [userAuth?.customerId, navigate, userAuth]);

  if (!customerContext) {
    return null;
  }

  return <>{children}</>;
});

function tokenToAuth(token: string): Auth {
  const decoded: any = jwtDecode(token);
  return {
    teamId: decoded.team_id,
    userEmail: decoded.email,
    token,
    expiration: Temporal.Instant.fromEpochSeconds(decoded.exp),
  };
}

export const AuthProvider = memo(function AuthProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const [authToken, setAuthToken] = useStore(authTokenStore);
  const auth = useMemo(
    () => (authToken === undefined ? undefined : tokenToAuth(authToken)),
    [authToken],
  );

  useEffect(() => {
    if (!auth) {
      return;
    }
    const duration = auth.expiration.since(Temporal.Now.instant());
    if (duration.sign < 0) {
      setAuthToken(undefined);
      return;
    }
    const timeout = setTimeout(
      () => setAuthToken(undefined),
      Math.min(2_147_483_647, duration.total("milliseconds")),
    );
    return () => clearTimeout(timeout);
  }, [auth, setAuthToken]);

  return (
    <AuthContext.Provider value={auth}>
      <SetAuthContext.Provider value={setAuthToken}>
        {children}
      </SetAuthContext.Provider>
    </AuthContext.Provider>
  );
});
